import { Component, inject, OnInit } from '@angular/core';
import { AsyncPipe, CurrencyPipe } from '@angular/common';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { combineLatest, map, startWith } from 'rxjs';
import { Button } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ChipModule } from 'primeng/chip';
import { SharedConfirmDialogComponent } from '@tangible/shared';
import { ConfirmationService } from 'primeng/api';
import { Drawer } from 'primeng/drawer';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { Tooltip } from 'primeng/tooltip';
import {
  CONSUMABLE_UNITS,
  Consumable,
  ConsumableUnit,
  consumableUnitLabel,
} from '../../models/consumable.model';
import { isPermissionError, parseApiError } from '../quotes-api-error';
import { ConsumableService } from '../consumable.service';
import { SettingsService } from '../settings-page/settings-service/settings.service';

@Component({
  selector: 'app-consumables-page',
  imports: [
    AsyncPipe,
    Button,
    CheckboxModule,
    ChipModule,
    SharedConfirmDialogComponent,
    CurrencyPipe,
    Drawer,
    FloatLabelModule,
    IconFieldModule,
    InputIconModule,
    InputNumberModule,
    InputTextModule,
    Message,
    ReactiveFormsModule,
    SelectModule,
    TextareaModule,
    Tooltip,
  ],
  providers: [ConfirmationService],
  templateUrl: './consumables-page.component.html',
})
export class ConsumablesPageComponent implements OnInit {
  private consumableService = inject(ConsumableService);
  private settingsService = inject(SettingsService);
  private confirmationService = inject(ConfirmationService);

  searchQuery = new FormControl('', { nonNullable: true });
  includeArchived = new FormControl(false, { nonNullable: true });
  isDrawerVisible = false;
  consumableInDrawer: Consumable | null = null;
  saveError: string | null = null;

  readonly unitOptions = [...CONSUMABLE_UNITS];
  readonly unitLabel = consumableUnitLabel;

  consumableForm = new FormGroup({
    name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)],
    }),
    cost: new FormControl<number | null>(null, {
      validators: [Validators.required, Validators.min(0)],
    }),
    unit: new FormControl<ConsumableUnit>('unit', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    description: new FormControl('', { nonNullable: true }),
  });

  showError(field: 'name' | 'cost'): boolean {
    const control = this.consumableForm.controls[field];
    return control.invalid && control.touched;
  }

  currencyCode$ = this.settingsService.settings$.pipe(
    map((settings) => settings?.organisation_base_currency_code || 'GBP'),
  );

  tableConsumables$ = combineLatest([
    this.consumableService.consumables$,
    this.searchQuery.valueChanges.pipe(startWith('')),
    this.includeArchived.valueChanges.pipe(startWith(false)),
  ]).pipe(
    map(([rows, query, includeArchived]) => {
      const needle = query.trim().toLowerCase();
      return rows.filter(
        (row) =>
          (includeArchived || !row.is_archived) &&
          `${row.name} ${row.description}`.toLowerCase().includes(needle),
      );
    }),
  );

  ngOnInit(): void {
    void this.consumableService.loadConsumables();
  }

  openDrawer(consumable?: Consumable): void {
    this.saveError = null;
    this.consumableInDrawer = consumable ?? null;
    this.consumableForm.reset({
      name: consumable?.name ?? '',
      cost: consumable ? Number(consumable.cost) : null,
      unit: consumable?.unit ?? 'unit',
      description: consumable?.description ?? '',
    });
    this.isDrawerVisible = true;
  }

  closeDrawer(): void {
    this.isDrawerVisible = false;
    this.consumableInDrawer = null;
    this.saveError = null;
  }

  async save(): Promise<void> {
    if (this.consumableForm.invalid) {
      this.consumableForm.markAllAsTouched();
      return;
    }
    const value = this.consumableForm.getRawValue();
    const payload = {
      name: value.name.trim(),
      cost: (value.cost ?? 0).toFixed(2),
      unit: value.unit,
      description: value.description.trim(),
    };
    this.saveError = null;
    try {
      if (this.consumableInDrawer) {
        await this.consumableService.updateConsumable(
          this.consumableInDrawer.id,
          payload,
        );
      } else {
        await this.consumableService.createConsumable(payload);
      }
      this.closeDrawer();
    } catch (error) {
      if (isPermissionError(error)) {
        return;
      }
      this.saveError = parseApiError(error, 'Unable to save this consumable.');
    }
  }

  confirmArchive(consumable: Consumable, archive: boolean): void {
    this.confirmationService.confirm({
      message: archive
        ? `Archive ${consumable.name}? It will stay on existing quotes.`
        : `Restore ${consumable.name} to the catalog?`,
      header: archive ? 'Archive consumable' : 'Restore consumable',
      accept: () => {
        void this.handleArchive(consumable, archive);
      },
    });
  }

  private async handleArchive(
    consumable: Consumable,
    archive: boolean,
  ): Promise<void> {
    this.saveError = null;
    try {
      await this.consumableService.updateConsumable(consumable.id, {
        is_archived: archive,
      });
    } catch (error) {
      if (isPermissionError(error)) {
        return;
      }
      this.saveError = parseApiError(error, 'Unable to save this consumable.');
    }
  }
}
